import React, { createContext, useContext, useState, useMemo, type ReactNode } from 'react';
import { useAmbilight } from '../hooks/useAmbilight';
import type {
  AlgorithmId,
  AlgorithmTune,
  AnimationMode,
  LightPoint,
  RGB,
  WizBulb,
  ZoneId,
} from '../types/ambilight';

export interface AmbilightContextValue {
  // Estado de puntos y focos
  points: LightPoint[];
  bulbs: WizBulb[];
  primaryBulb: WizBulb;
  selectedPointId: ZoneId | null;
  selectedPoint: LightPoint | undefined;

  // Algoritmos y sintonía
  algorithm: AlgorithmId;
  tune: AlgorithmTune;
  animationMode: AnimationMode;
  isRaw: boolean;
  globalBorderWidth: number;

  // Telemetría y captura
  isCapturing: boolean;
  fps: number;
  masterColor: RGB;
  inspectorZone: ZoneId;

  // Visibilidad de UI / Modales
  showZones: boolean;
  isLightManagerOpen: boolean;
  isTuneOpen: boolean;
  isZonePreviewOpen: boolean;

  // Setters y Toggles
  setSelectedPointId: (id: ZoneId | null) => void;
  selectPoint: (pointId: ZoneId | string | null) => void;
  setAlgorithm: (algo: AlgorithmId) => void;
  updateTuneParam: (key: keyof AlgorithmTune, val: number | boolean) => void;
  updatePointBorderWidth: (pointId: string, width: number) => void;
  applyBorderToAll: (width: number) => void;
  setAnimationMode: React.Dispatch<React.SetStateAction<AnimationMode>>;
  toggleRaw: () => void;
  toggleShareScreen: () => Promise<void>;
  setInspectorZone: (zone: ZoneId) => void;
  setShowZones: React.Dispatch<React.SetStateAction<boolean>>;
  toggleZones: () => void;
  setIsLightManagerOpen: (open: boolean) => void;
  setIsTuneOpen: (open: boolean) => void;
  setIsZonePreviewOpen: (open: boolean) => void;
  toggleZonePreview: () => void;

  // Asignaciones de focos WiZ
  assignBulbToPoint: (pointId: string, bulbId: string | null) => void;
  addBulb: (name: string, ip: string) => Promise<boolean>;
  removeBulb: (bulbId: string) => void;
  setPrimaryBulb: (bulbId: string) => void;
  togglePointAssignment: (bulbId: string, pointId: ZoneId) => void;
  assignAllPointsToBulb: (bulbId: string) => void;
  unassignAllPointsFromBulb: (bulbId: string) => void;
  updateBulbMaxBrightness: (bulbId: string, maxBrightness: number) => void;
  globalMaxBrightness: number;
  updateGlobalMaxBrightness: (val: number) => void;

  // Consultas directas de luces correspondientes
  getBulbForPoint: (pointId: ZoneId) => WizBulb | undefined;
  getBulbNamesForPoint: (pointId: ZoneId) => string;
  getBulbCountForPoint: (pointId: ZoneId) => number;

  // Persistencia explícita en localhost
  saveStatus: string | null;
  saveConfigToLocalStorage: () => void;
}

import { AmbilightContext } from './useAmbilightContext';

export const AmbilightProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const ambilight = useAmbilight();

  const [selectedPointId, setSelectedPointId] = useState<ZoneId | null>(null);
  const [showZones, setShowZones] = useState<boolean>(false);
  const [isLightManagerOpen, setIsLightManagerOpen] = useState<boolean>(false);
  const [isTuneOpen, setIsTuneOpen] = useState<boolean>(false);
  const [isZonePreviewOpen, setIsZonePreviewOpen] = useState<boolean>(false);

  const selectedPoint = useMemo(() => {
    return ambilight.points.find((p) => p.id === selectedPointId);
  }, [ambilight.points, selectedPointId]);

  const selectPoint = (pointId: ZoneId | string | null) => {
    const zid = (pointId as ZoneId) || null;
    setSelectedPointId(zid);
    if (zid) {
      ambilight.setInspectorZone(zid);
    }
  };

  const toggleRaw = () => {
    ambilight.setAnimationMode((prev: AnimationMode) => (prev === 'raw' ? 'optimal' : 'raw'));
  };

  const toggleZones = () => {
    setShowZones((prev) => !prev);
  };

  const toggleZonePreview = () => {
    setIsZonePreviewOpen((prev) => !prev);
  };

  const getBulbForPoint = (pointId: ZoneId): WizBulb | undefined => {
    const pt = ambilight.points.find((p) => p.id === pointId);
    if (!pt || !pt.assignedBulbId) return undefined;
    return ambilight.bulbs.find((bulb) => bulb.id === pt.assignedBulbId);
  };

  const getBulbNamesForPoint = (pointId: ZoneId): string => {
    const pt = ambilight.points.find((p) => p.id === pointId);
    if (!pt || !pt.assignedBulbId) return 'Sin luz asignada';
    const b = ambilight.bulbs.find((bulb) => bulb.id === pt.assignedBulbId);
    return b ? b.name : 'Sin luz asignada';
  };

  const getBulbCountForPoint = (pointId: ZoneId): number => {
    const pt = ambilight.points.find((p) => p.id === pointId);
    if (!pt || !pt.assignedBulbId) return 0;
    const exists = ambilight.bulbs.some((bulb) => bulb.id === pt.assignedBulbId);
    return exists ? 1 : 0;
  };

  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  const saveConfigToLocalStorage = () => {
    try {
      localStorage.setItem('wiz_bulbs_config', JSON.stringify(ambilight.bulbs));
      const pointsConfig = ambilight.points.map((p) => ({
        id: p.id,
        borderWidth: p.borderWidth,
        assignedBulbId: p.assignedBulbId,
      }));
      localStorage.setItem('wiz_points_config', JSON.stringify(pointsConfig));
      localStorage.setItem('wiz_algorithm_config', ambilight.algorithm);
      localStorage.setItem('wiz_tune_config', JSON.stringify(ambilight.tune));
      localStorage.setItem('wiz_border_config', String(ambilight.globalBorderWidth));
      localStorage.setItem('wiz_mode_config', ambilight.animationMode);
      setSaveStatus('Guardado ✓');
      setTimeout(() => setSaveStatus(null), 2500);
    } catch {
      setSaveStatus('Error');
      setTimeout(() => setSaveStatus(null), 2500);
    }
  };

  const value: AmbilightContextValue = {
    ...ambilight,
    isRaw: ambilight.animationMode === 'raw',
    selectedPointId,
    selectedPoint,
    showZones,
    isLightManagerOpen,
    isTuneOpen,
    isZonePreviewOpen,
    setSelectedPointId,
    selectPoint,
    toggleRaw,
    toggleZones,
    toggleZonePreview,
    setIsLightManagerOpen,
    setIsTuneOpen,
    setIsZonePreviewOpen,
    setShowZones,
    getBulbForPoint,
    getBulbNamesForPoint,
    getBulbCountForPoint,
    saveStatus,
    saveConfigToLocalStorage,
  };

  return <AmbilightContext.Provider value={value}>{children}</AmbilightContext.Provider>;
};

export { useAmbilightContext } from './useAmbilightContext';
